import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils/utils";

const buttonVariants = cva(
	"relative btn inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none ring-0 ring-primary/5 focus-visible:ring-1 focus-visible:ring-white disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-98 rounded border border-x-gray-200 border-t-black/1 border-b-black/20",
	{
		variants: {
			variant: {
				default:
					"font-primary shadow-xs not-hover:bg-gradient-to-r from-secondary to-primary/5 hover:bg-gray-200",
				destructive:
					"highlight text-destructive-foreground shadow-xs not-hover:bg-gradient-to-r from-secondary to-destructive/5 hover:bg-destructive/40",
				outline:
					"border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
				secondary:
					"font-primary shadow not-hover:bg-gradient-to-r from-secondary to-primary/5 hover:bg-gray-200",
				hero:
					"highlight font-primary shadow-sm hover:font-primary-foreground not-hover:bg-gradient-to-r from-secondary to-primary/5 hover:bg-blue-300",
				ghost: "hover:bg-accent hover:text-accent-foreground border-0",
				link: "font-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 rounded-md px-3 text-xs",
				lg: "h-10 rounded-md px-8",
				icon: "h-9 w-9 cursor-pointer",
				none: "p-1 m-0 text-xs h-4 w-4 cursor-pointer",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
